import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { CheckCircle, Circle, ArrowLeft, Package, Wallet, Receipt, ListChecks, SignOut } from "@phosphor-icons/react";

const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

export default function ExitChecklist() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const load = () => api.get(`/exit-checklist/${id}`).then(({ data }) => setC(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!c) return <div className="text-sm text-zinc-500">Loading…</div>;
  const e = c.employee;
  return (
    <div className="space-y-6" data-testid="exit-checklist">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Link to="/app/employees" className="text-xs text-zinc-500 hover:underline inline-flex items-center gap-1" data-testid="exit-back"><ArrowLeft size={12} /> Employees</Link>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 mt-1">Offboarding</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Exit checklist · {e.name}</h1>
          <p className="text-sm text-zinc-500 mt-2"><span className="font-mono-plex">{e.employee_code}</span> · {e.designation || "—"} · {e.department || "—"}
            {e.last_working_date ? <> · Last working date <b>{e.last_working_date}</b> ({e.separation_type || "—"})</> : <> · <span className="text-amber-700">last working date not recorded</span></>}</p>
        </div>
        <div className="border border-zinc-200 bg-white rounded-sm px-5 py-3 min-w-[200px]" data-testid="exit-progress">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Progress</div>
          <div className="font-display text-3xl font-bold">{c.progress.done}<span className="text-zinc-400 text-lg">/{c.progress.total}</span></div>
          <div className="h-1.5 bg-zinc-100 rounded-full mt-2"><div className="h-1.5 rounded-full bg-emerald-600 transition-all" style={{ width: `${c.progress.pct}%` }} /></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="border border-zinc-200 bg-white rounded-sm p-5 lg:col-span-2 space-y-2" data-testid="exit-steps">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1"><ListChecks size={14} /> Checklist</div>
          {c.steps.map((s) => (
            <div key={s.key} className="flex items-center gap-2 text-sm py-1.5" data-testid={`exit-step-${s.key}`}>
              {s.done ? <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0" /> : <Circle size={20} className="text-zinc-300 shrink-0" />}
              <span className={s.done ? "text-zinc-500 line-through" : "text-zinc-900"}>{s.label}</span>
            </div>
          ))}
          {e.status !== "exited" && e.status !== "inactive" && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2 mt-2">Record the separation first from Employees → <b>Mark exited</b>.</div>}
        </div>
        <div className="lg:col-span-3 space-y-4">
          <Tasks c={c} reload={load} />
          <Assets c={c} reload={load} />
          <Settlement c={c} reload={load} />
        </div>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, right, children, testid }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-5 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between"><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1"><Icon size={14} /> {title}</div>{right}</div>
      {children}
    </div>
  );
}

function Tasks({ c, reload }) {
  const start = async () => { try { await api.post(`/exit-checklist/${c.employee.id}/start`); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const toggle = async (t) => { try { await api.patch(`/workflows/${c.workflow.id}/tasks`, { task_key: t.key, completed: t.status !== "done" }); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <Card title="Offboarding tasks" icon={SignOut} testid="exit-tasks" right={!c.workflow && <Button size="sm" onClick={start} className="btn-zenith rounded-sm h-7" data-testid="exit-start-workflow">Start offboarding</Button>}>
      {!c.workflow ? <div className="text-xs text-zinc-500">No offboarding workflow yet — start it to track knowledge transfer, access revocation and letters.</div> : (
        <div className="space-y-1">
          {c.workflow.tasks.map((t) => (
            <label key={t.key} className="flex items-center gap-2 text-sm py-1 cursor-pointer" data-testid={`exit-task-${t.key}`}>
              <input type="checkbox" checked={t.status === "done"} onChange={() => toggle(t)} className="accent-emerald-600" />
              <span className={t.status === "done" ? "line-through text-zinc-400" : ""}>{t.title}</span>
              <Badge variant="outline" className="rounded-sm text-[10px] ml-auto">{t.owner_role}</Badge>
            </label>
          ))}
        </div>
      )}
    </Card>
  );
}

function Assets({ c, reload }) {
  const ret = async (a) => {
    try { await api.post(`/assets/${a.id}/return`, { returned_on: new Date().toISOString().slice(0, 10) }); toast.success(`${a.label} marked returned`); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card title={`Assets (${c.pending_assets} pending)`} icon={Package} testid="exit-assets">
      {c.assets.length === 0 ? <div className="text-xs text-zinc-500">No assets were issued to this employee.</div> : (
        <table className="hr-table text-xs"><thead><tr><th>Asset</th><th>Serial</th><th>Issued</th><th>Status</th><th></th></tr></thead>
          <tbody>{c.assets.map((a) => (
            <tr key={a.id} data-testid={`exit-asset-${a.id}`}>
              <td>{a.label} <span className="text-zinc-400">({a.kind})</span></td><td className="font-mono-plex">{a.serial_number || "—"}</td><td className="font-mono-plex">{a.issued_on}</td>
              <td><Badge variant="outline" className={`rounded-sm ${a.status === "returned" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>{a.status}{a.returned_on ? ` ${a.returned_on}` : ""}</Badge></td>
              <td>{a.status === "issued" && <button onClick={() => ret(a)} className="text-xs text-emerald-700 hover:underline" data-testid={`exit-asset-return-${a.id}`}>Mark returned</button>}</td>
            </tr>))}</tbody></table>
      )}
    </Card>
  );
}

function Settlement({ c, reload }) {
  const [f, setF] = useState({ include_encashment: true, notice_period_recovery: 0, gratuity: 0, bonus: 0, other_earnings: 0, other_deductions: 0 });
  const [busy, setBusy] = useState(false);
  const draft = async () => {
    setBusy(true);
    try { await api.post(`/exit-checklist/${c.employee.id}/fnf-draft`, { ...f, notice_period_recovery: +f.notice_period_recovery, gratuity: +f.gratuity, bonus: +f.bonus, other_earnings: +f.other_earnings, other_deductions: +f.other_deductions }); toast.success("F&F draft created"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const act = async (action) => { try { await api.patch(`/fnf/${c.fnf.id}`, { action }); toast.success(action === "finalize" ? "F&F finalised" : "F&F marked paid"); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const enc = c.encashment;
  return (
    <Card title="Dues & Full and Final settlement" icon={Wallet} testid="exit-settlement">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
        <div className="border border-zinc-100 bg-zinc-50 rounded-sm p-2"><div className="text-zinc-500">Leave encashment (est.)</div><div className="font-mono-plex font-semibold" data-testid="exit-encashment">{inr(enc.amount)}</div><div className="text-[10px] text-zinc-500">{enc.days} day(s) × {inr(enc.per_day)}/day{enc.items.map((i) => ` · ${i.leave_type} ${i.days}`).join("")}</div></div>
        <div className="border border-zinc-100 bg-zinc-50 rounded-sm p-2"><div className="text-zinc-500">Loans outstanding</div><div className="font-mono-plex font-semibold" data-testid="exit-loans">{inr(c.loan_outstanding)}</div><div className="text-[10px] text-zinc-500">{c.loans.length} active loan(s) — recovered in F&F</div></div>
        <div className="border border-zinc-100 bg-zinc-50 rounded-sm p-2"><div className="text-zinc-500">F&F status</div><div className="font-semibold" data-testid="exit-fnf-status">{c.fnf ? c.fnf.status : "not started"}</div>{c.fnf && <div className="text-[10px] text-zinc-500">Net payable {inr(c.fnf.net_payable)}</div>}</div>
      </div>
      {!c.fnf ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[["notice_period_recovery", "Notice recovery"], ["gratuity", "Gratuity"], ["bonus", "Bonus"], ["other_earnings", "Other earnings"], ["other_deductions", "Other deductions"]].map(([k, l]) => (
              <div key={k}><Label className="text-[10px]">{l}</Label><Input type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="rounded-sm h-8 font-mono-plex text-xs" data-testid={`exit-fnf-${k}`} /></div>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={f.include_encashment} onChange={(e) => setF({ ...f, include_encashment: e.target.checked })} data-testid="exit-fnf-encash" /> Include leave encashment {inr(enc.amount)}</label>
            <Button size="sm" onClick={draft} disabled={busy || !["exited", "inactive"].includes(c.employee.status)} className="btn-zenith rounded-sm h-8" title={!["exited", "inactive"].includes(c.employee.status) ? "Mark the employee exited first" : ""} data-testid="exit-fnf-create"><Receipt size={14} className="mr-1" /> Create F&F draft</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between text-xs">
          <div>Earnings {inr(c.fnf.total_earnings)} − Deductions {inr(c.fnf.total_deductions)} = <b className="text-emerald-700">{inr(c.fnf.net_payable)}</b></div>
          <div className="flex gap-2">
            {c.fnf.status === "paid" && <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200" data-testid="exit-fnf-paid-badge">Paid</Badge>}
            {c.fnf.status === "draft" && <Button size="sm" variant="outline" onClick={() => act("finalize")} className="rounded-sm h-8" data-testid="exit-fnf-finalize">Finalise</Button>}
            {c.fnf.status === "finalized" && <Button size="sm" onClick={() => act("mark_paid")} className="btn-zenith rounded-sm h-8" data-testid="exit-fnf-paid">Mark paid</Button>}
          </div>
        </div>
      )}
    </Card>
  );
}
